// Active Quotes and Projects dashboard panel (Session AAJ, Item 2). Two sub-sections in one
// card, divided by a visible rule: maintenance (ESA) quotes in draft/pending, and active
// projects whose quotation is not yet signed. Self fetches (its own loading/error). Works
// classification, project status and quotation status are rendered via the shared projects UI
// helpers. Ref/View link to the Quote Hub (Session AQW: V2 now has a per-quote detail route,
// /office/quotes/:id); a draft quote's "Resume" reopens the calculator pre-loaded, the same flow
// as the quotes list Edit action.
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import type { ActiveQuotesAndProjects, DashboardMaintenanceQuote, DashboardActiveProject } from "../../lib/api";
import type { WorksClassification } from "../../lib/api";
import { WORKS_TYPE_LABELS } from "./projectsUi";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";

function gbp(n: number | null): string {
  if (n == null) return "-";
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

function SectionShell({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <h3 className="ele-section-title">{title}</h3>
        <span className="rounded-full bg-ele-inset px-2 py-0.5 font-menu text-xs text-ele-strong">{count}</span>
      </div>
      {children}
    </div>
  );
}

function QuotesSubSection({ quotes, mobileCards }: { quotes: DashboardMaintenanceQuote[]; mobileCards: boolean }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);

  // Reopen a draft quote in the calculator, pre-loaded with its stored state (same flow as the
  // quotes list Edit action). The calculator revises the quote in place on save.
  async function resume(id: string, ref: string) {
    setBusy(id);
    try {
      // Live, never the request cache (Session ARU), as the quotes-list and quote-page Edit do.
      const q = await api.quotes.get(id, { noCache: true });
      const calculatorState = (q.payload as { calculatorState?: unknown } | null)?.calculatorState;
      // status rides along (Session AAV) so the calculator's autosave applies to this resumed
      // draft exactly as it does from the quotes-list Edit action.
      // Session AHP: the stored payload rides along so the calculator can rebuild the form for a
      // quote that has no calculatorState (the MCP create_quote tool writes none), exactly as the
      // quotes-list Edit action does. Both entry points shared the same defect.
      navigate("/office/quotes/new", {
        state: { resume: { id, ref, status: q.status, calculatorState: calculatorState ?? undefined, payload: q.payload } },
      });
    } catch {
      setBusy(null);
    }
  }

  // Shared between the card and table branches so the two never drift apart.
  const viewAllLink =
    quotes.length === 5 ? (
      <div className="mt-2 text-right">
        <Link to="/office/quotes" className="ele-view-all">
          View all quotes &#8594;
        </Link>
      </div>
    ) : null;

  return (
    <SectionShell title="Maintenance Quotes" count={quotes.length}>
      {quotes.length === 0 ? (
        <p className="text-sm text-ele-body">None active.</p>
      ) : mobileCards ? (
        <>
          {/* Session AHB pattern: Customer/Site is the primary scan field (what the whole card
              taps through to -- the quote, mirroring the row's own onClick target); Ref is the
              second identifying field; Sent is a date (meta, only shown once the quote has been
              sent); Value and Status stack in the right rail. A draft row's Resume keeps its own
              async reopen-in-calculator behaviour in the actions row; a non-draft row has no
              separate action, since the card itself already taps through to the same place the
              desktop "View" link goes. */}
          <MobileCardList label="Maintenance quotes">
            {quotes.map((q) => (
              <MobileListCard
                key={q.id}
                to={`/office/quotes/${q.id}`}
                primary={q.customerName ?? q.siteName ?? "Not linked"}
                secondary={<span className="ele-ref">{q.ref}</span>}
                meta={q.status === "pending" && q.sentAt ? `Sent ${(q.sentAt ? formatDate(q.sentAt) : "")}` : undefined}
                right={
                  <>
                    {q.annualValue != null && <span className="font-semibold text-ele-strong">{gbp(q.annualValue)}/yr</span>}
                    <StatusPill domain="quote" status={q.status} />
                  </>
                }
                actions={
                  q.status === "draft" ? (
                    <Button variant="secondary" size="sm" className="flex-1" disabled={busy === q.id} onClick={() => resume(q.id, q.ref)}>
                      Resume
                    </Button>
                  ) : undefined
                }
              />
            ))}
          </MobileCardList>
          {viewAllLink}
        </>
      ) : (
        <>
          <div data-safetynet className="overflow-x-auto">
            {/* Session ACW Loop 6: Ref / Customer-Site / Status are the priority columns below md
                (identity + state); Value, Sent and the Resume/View action fold away, mirroring how
                ContractsPage moves its row actions to the destination the row now navigates to.
                Every row already carries a Link to /office/quotes (Ref, and View on a non-draft
                row), so the guarded whole-row onClick reuses that exact target rather than
                inventing a new one; a draft row's Resume button is excluded by the guard and keeps
                its own async reopen-in-calculator behaviour. */}
            <table className="ele-list-table w-full text-left text-sm">
              <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                <tr>
                  <th className="px-3 py-2">Ref</th>
                  <th className="px-3 py-2">Customer / Site</th>
                  <th className="max-md:hidden px-3 py-2">Value</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="max-md:hidden px-3 py-2">Sent</th>
                  <th className="cell-actions max-md:hidden px-3 py-2" aria-label="Action" />
                  <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr
                    key={q.id}
                    className="group cursor-pointer border-t border-ele-purple-accent transition hover:bg-ele-row-hover"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest("a,button,input,select,textarea,label")) return;
                      navigate(`/office/quotes/${q.id}`);
                    }}
                  >
                    <td className="px-3 py-2 align-top">
                      {/* Reference data is blue (Session ACG): quote refs in the monospace
                          reference treatment. The ?q= pre-fill points the quotes hub at this
                          specific quote rather than the bare list. */}
                      <Link to={`/office/quotes/${q.id}`} className="ele-ref underline-offset-2 hover:underline">
                        {q.ref}
                      </Link>
                    </td>
                    <td className="px-3 py-2 align-top">{q.customerName ?? q.siteName ?? "Not linked"}</td>
                    <td className="max-md:hidden px-3 py-2 align-top">{q.annualValue == null ? "-" : `${gbp(q.annualValue)}/yr`}</td>
                    <td className="px-3 py-2 align-top">
                      <StatusPill domain="quote" status={q.status} />
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-top text-ele-body">{q.status === "pending" ? (q.sentAt ? formatDate(q.sentAt) : "") : ""}</td>
                    <td className="cell-actions max-md:hidden px-3 py-2 align-top text-right">
                      {q.status === "draft" ? (
                        <Button variant="secondary" size="sm" disabled={busy === q.id} onClick={() => resume(q.id, q.ref)}>
                          Resume
                        </Button>
                      ) : (
                        <Link to={`/office/quotes/${q.id}`} className="font-menu text-[10px] tracking-wide text-ele-green hover:underline">
                          View
                        </Link>
                      )}
                    </td>
                    <td className="cell-actions md:hidden pr-1 text-right">
                      <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                        <path d="M6 4l4 4-4 4z" />
                      </svg>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {viewAllLink}
        </>
      )}
    </SectionShell>
  );
}

function ProjectsSubSection({ projects, mobileCards }: { projects: DashboardActiveProject[]; mobileCards: boolean }) {
  const navigate = useNavigate();
  // Shared between the card and table branches so the two never drift apart.
  const viewAllLink =
    projects.length === 5 ? (
      <div className="mt-2 text-right">
        <Link to="/office/projects" className="ele-view-all">
          View all projects &#8594;
        </Link>
      </div>
    ) : null;

  return (
    <SectionShell title="Projects" count={projects.length}>
      {projects.length === 0 ? (
        <p className="text-sm text-ele-body">None active.</p>
      ) : mobileCards ? (
        <>
          {/* Session AHB pattern: Site is the primary scan field (what the whole card taps
              through to -- the project, mirroring the row's own onClick target); Ref is the
              second identifying field; Stage and Value stack in the right rail; Works
              classification and the quotation status, both folded away on the desktop table
              below md too, get a details block rather than being dropped. */}
          <MobileCardList label="Projects">
            {projects.map((p) => (
              <MobileListCard
                key={p.id}
                to={`/office/projects/${encodeURIComponent(p.id)}`}
                primary={p.siteName ?? "Not linked"}
                secondary={<span className="ele-ref">{p.ref}</span>}
                right={
                  <>
                    <StatusPill domain="project" status={p.projectStatus} />
                    {p.projectValue != null && <span className="text-sm text-ele-strong">{gbp(p.projectValue)}</span>}
                  </>
                }
                details={
                  <p className="flex flex-wrap items-center gap-2 text-sm text-ele-body">
                    <span>
                      {p.worksClassification
                        ? (WORKS_TYPE_LABELS[p.worksClassification as WorksClassification] ?? p.worksClassification)
                        : "Works not set"}
                    </span>
                    <StatusPill domain="projectQuotation" status={p.quotationStatus ?? "none"} />
                  </p>
                }
              />
            ))}
          </MobileCardList>
          {viewAllLink}
        </>
      ) : (
        <>
          <div data-safetynet className="overflow-x-auto">
            {/* Session ACW Loop 6: Ref / Site / Stage are the priority columns below md (identity +
                progress); Works, Value and Quote fold away. A project row already links its Ref
                cell to a real detail page, so the guarded whole-row onClick reuses that exact
                target (the ContractsPage pattern) -- the whole row becomes the tap target on a
                phone, not just the small Ref link. */}
            <table className="ele-list-table w-full text-left text-sm">
              <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                <tr>
                  <th className="px-3 py-2">Ref</th>
                  <th className="px-3 py-2">Site</th>
                  <th className="max-md:hidden px-3 py-2">Works</th>
                  <th className="max-md:hidden px-3 py-2">Value</th>
                  <th className="max-md:hidden px-3 py-2">Quote</th>
                  <th className="px-3 py-2">Stage</th>
                  <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr
                    key={p.id}
                    className="group cursor-pointer border-t border-ele-purple-accent transition hover:bg-ele-row-hover"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest("a,button,input,select,textarea,label")) return;
                      navigate(`/office/projects/${encodeURIComponent(p.id)}`);
                    }}
                  >
                    <td className="px-3 py-2 align-top">
                      <Link to={`/office/projects/${encodeURIComponent(p.id)}`} className="ele-ref underline-offset-2 hover:underline">
                        {p.ref}
                      </Link>
                    </td>
                    <td className="px-3 py-2 align-top">{p.siteName ?? "Not linked"}</td>
                    <td className="max-md:hidden px-3 py-2 align-top text-ele-body">
                      {p.worksClassification
                        ? (WORKS_TYPE_LABELS[p.worksClassification as WorksClassification] ?? p.worksClassification)
                        : "Not set"}
                    </td>
                    <td className="max-md:hidden px-3 py-2 align-top">{gbp(p.projectValue)}</td>
                    <td className="max-md:hidden px-3 py-2 align-top">
                      <StatusPill domain="projectQuotation" status={p.quotationStatus ?? "none"} />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <StatusPill domain="project" status={p.projectStatus} />
                    </td>
                    <td className="cell-actions md:hidden pr-1 text-right">
                      <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                        <path d="M6 4l4 4-4 4z" />
                      </svg>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {viewAllLink}
        </>
      )}
    </SectionShell>
  );
}

export function ActiveQuotesPanel() {
  const [data, setData] = useState<ActiveQuotesAndProjects | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism),
  // shared by both sub-sections so they never disagree on layout mid-render.
  const [mobileCards] = useState(initialMobileLayout);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    api.dashboard
      .activeQuotes()
      .then((d) => {
        if (live) setData(d);
      })
      .catch(() => {
        if (live) setError(true);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [reloadKey]);

  return (
    <section className="mb-6 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <h2 className="ele-section-title mb-3">Active Quotes and Projects</h2>

      {loading ? (
        // The shared loading language: an announced skeleton (Loader carries the status region).
        <Loader label="Loading quotes and projects..." rows={3} />
      ) : error ? (
        <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
          <p className="text-ele-strong">Could not load quotes and projects.</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={() => setReloadKey((k) => k + 1)}>
            Retry
          </Button>
        </div>
      ) : data ? (
        <div className="space-y-4">
          <QuotesSubSection quotes={data.maintenanceQuotes} mobileCards={mobileCards} />
          <div className="border-t border-ele-divider" />
          <ProjectsSubSection projects={data.activeProjects} mobileCards={mobileCards} />
        </div>
      ) : null}
    </section>
  );
}
